import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Separator } from "./ui/separator";
import { Badge } from "./ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { Alert, AlertDescription } from "./ui/alert";
import { Save, Trash2, Plus, X, Download, Crown, Zap } from "lucide-react";
import { toast } from "sonner";
import { useProfile } from "../src/hooks/useProfile";
import { useTheme } from "./ThemeProvider";
import { useDataManagement } from "../src/hooks/useDataManagement";
import { useSubscription } from "../src/hooks/useSubscription";
import { DEFAULT_CONFIDENCE_RATING, DEFAULT_REVIEW_INTERVALS } from "../src/lib/user-settings";
import { invokeFunction } from "../src/integrations/neon/client";
import { AccountSettings } from "./AccountSettings";
import type { StoredUserSettings } from "../src/types";

interface SettingsProps {
  /** Opens the pricing page from the free-plan review-date cap. */
  onUpgrade?: () => void;
}

export function Settings({ onUpgrade }: SettingsProps) {
  const { profile, loading: profileLoading, error: profileError, updateProfile } = useProfile();
  const { getReviewDateLimit, canAddReviewDate } = useSubscription();
  const { theme, setTheme } = useTheme();
  const { loading: dataLoading, exportDecisions, clearAllData, deleteAccount } = useDataManagement();
  
  // Controlled so the profile-settings Save button below can be hidden on the
  // Account tab, whose two forms submit on their own.
  const [activeTab, setActiveTab] = useState("general");
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const [notifications, setNotifications] = useState({
    reviewDue: true,
    monthlyReport: true,
    generalUpdates: true
  });

  const [showReleaseNotesPopup, setShowReleaseNotesPopup] = useState(true);

  const [defaultReviewIntervals, setDefaultReviewIntervals] = useState(DEFAULT_REVIEW_INTERVALS);
  const [newInterval, setNewInterval] = useState("");
  
  const [preferences, setPreferences] = useState({
    timezone: "UTC",
    dateFormat: "MM/DD/YYYY",
    defaultRating: DEFAULT_CONFIDENCE_RATING,
    reminderFrequency: "daily"
  });

  const [customTags, setCustomTags] = useState(["product", "tech", "hiring", "design", "strategy"]);
  const [newTag, setNewTag] = useState("");

  // Load preferences from profile
  useEffect(() => {
    if (profile?.notification_preferences) {
      const prefs = profile.notification_preferences as StoredUserSettings;
      
      // Handle simplified notification structure directly from notification_preferences
      if (prefs.reviewDue !== undefined || prefs.monthlyReport !== undefined || prefs.generalUpdates !== undefined) {
        // New simplified structure
        setNotifications({
          reviewDue: prefs.reviewDue ?? true,
          monthlyReport: prefs.monthlyReport ?? true,
          generalUpdates: prefs.generalUpdates ?? true
        });
      } else if (prefs.notifications) {
        // Legacy structure - migrate to new format
        const legacyNotifs = prefs.notifications;
        setNotifications({
          reviewDue: legacyNotifs.reviewDue ?? true,
          monthlyReport: legacyNotifs.monthlyReport ?? true,
          generalUpdates: legacyNotifs.emailReminders ?? true
        });
      }
      
      if (prefs.defaultReviewIntervals) {
        setDefaultReviewIntervals(prefs.defaultReviewIntervals);
      }
      if (prefs.preferences) {
        setPreferences(prev => ({ ...prev, ...prefs.preferences }));
      }
      if (prefs.customTags) {
        setCustomTags(prefs.customTags);
      }
      if (prefs.showReleaseNotesPopup !== undefined) {
        setShowReleaseNotesPopup(prefs.showReleaseNotesPopup);
      }
    }
    if (profile?.timezone) {
      setPreferences(prev => ({ ...prev, timezone: profile.timezone || 'UTC' }));
    }
  }, [profile]);

  const reviewDateLimit = getReviewDateLimit();
  // Guards the button as well as the handler: a saved schedule can already be
  // over the cap (saved on a paid plan that has since lapsed), and that list is
  // shown as saved rather than silently trimmed.
  const canAddAnotherInterval = canAddReviewDate(defaultReviewIntervals.length);

  const addInterval = () => {
    if (!canAddAnotherInterval) return;
    const interval = parseInt(newInterval);
    if (interval && interval > 0 && !defaultReviewIntervals.includes(interval)) {
      setDefaultReviewIntervals([...defaultReviewIntervals, interval].sort((a, b) => a - b));
      setNewInterval("");
    }
  };

  const removeInterval = (intervalToRemove: number) => {
    setDefaultReviewIntervals(defaultReviewIntervals.filter(i => i !== intervalToRemove));
  };

  const addTag = () => {
    if (newTag && !customTags.includes(newTag)) {
      setCustomTags([...customTags, newTag]);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setCustomTags(customTags.filter(t => t !== tagToRemove));
  };

  const saveSettings = async () => {
    setIsSavingSettings(true);
    try {
      const settingsData = {
        timezone: preferences.timezone,
        notification_preferences: {
          // Store simplified notifications directly in notification_preferences
          ...notifications,
          // Keep other settings
          defaultReviewIntervals,
          preferences,
          customTags,
          showReleaseNotesPopup
        }
      };

      const { error } = await updateProfile(settingsData);

      if (error) {
        console.error('Save settings error:', error);
        toast.error("Failed to save settings: " + error.message);
        return;
      }

      toast.success("Settings saved successfully!");

      // Best effort, and deliberately after the success toast: the profile row
      // is the source of truth, and Loops being unreachable should not make a
      // saved setting look unsaved. Review reminders read the row directly, so
      // they are correct either way; only Loops-side campaign segmentation
      // lags until the next save.
      const { error: loopsError } = await invokeFunction('/loops/preferences', {
        body: notifications,
      });
      if (loopsError) {
        console.warn('Failed to sync email preferences to Loops:', loopsError);
      }
    } catch (error) {
      console.error('Save settings error:', error);
      toast.error("Failed to save settings");
    } finally {
      setIsSavingSettings(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1>Settings</h1>
          <p className="text-muted-foreground">
            Customize your DecisionLog experience
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="account">Account</TabsTrigger>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="defaults">Defaults</TabsTrigger>
            <TabsTrigger value="data">Data & Privacy</TabsTrigger>
          </TabsList>

          <TabsContent value="account" className="space-y-6">
            <AccountSettings />
          </TabsContent>

          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Appearance</CardTitle>
                <CardDescription>Customize how DecisionLog looks and feels</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="theme">Theme</Label>
                  <Select 
                    value={theme} 
                    onValueChange={(value: "light" | "dark") => setTheme(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dateFormat">Date Format</Label>
                  <Select 
                    value={preferences.dateFormat} 
                    onValueChange={(value) => setPreferences({...preferences, dateFormat: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select 
                    value={preferences.timezone} 
                    onValueChange={(value) => setPreferences({...preferences, timezone: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px] overflow-y-auto">
                      <SelectItem value="Pacific/Honolulu">Hawaii Standard Time (HST) - Honolulu</SelectItem>
                      <SelectItem value="America/Anchorage">Alaska Time (AKT) - Anchorage</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time (PT) - Los Angeles, Vancouver</SelectItem>
                      <SelectItem value="America/Denver">Mountain Time (MT) - Denver, Calgary</SelectItem>
                      <SelectItem value="America/Chicago">Central Time (CT) - Chicago, Mexico City</SelectItem>
                      <SelectItem value="America/New_York">Eastern Time (ET) - New York, Toronto</SelectItem>
                      <SelectItem value="America/Halifax">Atlantic Time (AT) - Halifax</SelectItem>
                      <SelectItem value="America/St_Johns">Newfoundland Time (NT) - St. John's</SelectItem>
                      <SelectItem value="America/Argentina/Buenos_Aires">Argentina Time - Buenos Aires</SelectItem>
                      <SelectItem value="America/Sao_Paulo">Brazil Time - São Paulo</SelectItem>
                      <SelectItem value="UTC">Greenwich Mean Time (GMT) - UTC</SelectItem>
                      <SelectItem value="Europe/London">Greenwich Mean Time (GMT) - London</SelectItem>
                      <SelectItem value="Europe/Berlin">Central European Time (CET) - Berlin, Paris, Rome</SelectItem>
                      <SelectItem value="Europe/Athens">Eastern European Time (EET) - Athens, Kyiv, Bucharest</SelectItem>
                      <SelectItem value="Europe/Moscow">Moscow Time (MSK) - Moscow</SelectItem>
                      <SelectItem value="Asia/Dubai">Gulf Standard Time (GST) - Dubai, Abu Dhabi</SelectItem>
                      <SelectItem value="Asia/Kolkata">India Standard Time (IST) - Delhi, Mumbai</SelectItem>
                      <SelectItem value="Asia/Dhaka">Bangladesh Time (BST) - Dhaka</SelectItem>
                      <SelectItem value="Asia/Bangkok">Indochina Time - Bangkok, Hanoi</SelectItem>
                      <SelectItem value="Asia/Shanghai">China Standard Time (CST) - Beijing, Shanghai</SelectItem>
                      <SelectItem value="Asia/Tokyo">Japan Standard Time (JST) - Tokyo</SelectItem>
                      <SelectItem value="Australia/Sydney">Australian Eastern Time - Sydney, Melbourne</SelectItem>
                      <SelectItem value="Pacific/Auckland">New Zealand Time - Auckland, Wellington</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Email Notifications</CardTitle>
                <CardDescription>Choose what email notifications you'd like to receive</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Review Reminders</Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified when decision reviews are due
                    </p>
                  </div>
                  <Switch
                    checked={notifications.reviewDue}
                    onCheckedChange={(checked) => 
                      setNotifications({...notifications, reviewDue: checked})
                    }
                  />
                </div>

                {notifications.reviewDue && (
                  <div className="rounded-lg border p-4 space-y-2">
                    <Label htmlFor="reminderFrequency">How often</Label>
                    <Select
                      value={preferences.reminderFrequency}
                      onValueChange={(value) => setPreferences({...preferences, reminderFrequency: value})}
                    >
                      <SelectTrigger id="reminderFrequency" className="sm:w-64">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly (Mondays)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      Sent once a day. Each review is only ever emailed about once, and
                      several due at the same time arrive as one email.
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Monthly Report</Label>
                    <p className="text-sm text-muted-foreground">
                      Monthly analytics and insights report
                    </p>
                  </div>
                  <Switch
                    checked={notifications.monthlyReport}
                    onCheckedChange={(checked) => 
                      setNotifications({...notifications, monthlyReport: checked})
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>General Updates</Label>
                    <p className="text-sm text-muted-foreground">
                      Product updates and feature announcements
                    </p>
                  </div>
                  <Switch
                    checked={notifications.generalUpdates}
                    onCheckedChange={(checked) => 
                      setNotifications({...notifications, generalUpdates: checked})
                    }
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Release Notes Popup</Label>
                    <p className="text-sm text-muted-foreground">
                      Show release notes popup when logging in
                    </p>
                  </div>
                  <Switch
                    checked={showReleaseNotesPopup}
                    onCheckedChange={setShowReleaseNotesPopup}
                  />
                </div>
              </CardContent>
            </Card>

          </TabsContent>

          <TabsContent value="defaults" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Default Review Intervals</CardTitle>
                <CardDescription>Set your preferred review schedule for new decisions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {defaultReviewIntervals.map((interval) => (
                    <Badge key={interval} variant="secondary" className="flex items-center gap-1">
                      {interval} days
                      <button
                        onClick={() => removeInterval(interval)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>

                {canAddAnotherInterval ? (
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Days"
                      value={newInterval}
                      onChange={(e) => setNewInterval(e.target.value)}
                      className="w-24"
                    />
                    <Button variant="outline" onClick={addInterval}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Interval
                    </Button>
                  </div>
                ) : (
                  <Alert className="border-yellow-200 bg-yellow-50">
                    <Zap className="h-4 w-4 text-yellow-500" />
                    <AlertDescription>
                      <div className="space-y-2">
                        <p className="font-medium text-yellow-800">
                          {reviewDateLimit} review dates per decision on the free plan
                        </p>
                        <p className="text-yellow-700">
                          Upgrade to schedule as many reviews as a decision needs.
                        </p>
                        {onUpgrade && (
                          <Button
                            onClick={onUpgrade}
                            variant="outline"
                            size="sm"
                            className="mt-2 border-yellow-300 text-yellow-800 hover:bg-yellow-100"
                          >
                            <Crown className="h-4 w-4 mr-2" />
                            Upgrade
                          </Button>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Default Settings</CardTitle>
                <CardDescription>Default values for new decisions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Default Confidence Rating</Label>
                  <Select 
                    value={preferences.defaultRating.toString()} 
                    onValueChange={(value) => setPreferences({...preferences, defaultRating: parseInt(value)})}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 10}, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {i + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Custom Tags</CardTitle>
                <CardDescription>Manage your frequently used decision tags</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {customTags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Input
                    placeholder="Add custom tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                  />
                  <Button variant="outline" onClick={addTag}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Tag
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="data" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Data Export</CardTitle>
                <CardDescription>Download your decision data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  variant="outline" 
                  onClick={() => exportDecisions('json')}
                  disabled={dataLoading}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export All Decisions (JSON)
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => exportDecisions('csv')}
                  disabled={dataLoading}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export All Decisions (CSV)
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Data Management</CardTitle>
                <CardDescription>Manage your account data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Your data is automatically backed up and synced across devices.
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" disabled={dataLoading}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Clear All Data
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Clear All Data</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete all your decisions, tags, emotions, and reviews. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={clearAllData}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Clear All Data
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>

            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="text-destructive">Danger Zone</CardTitle>
                <CardDescription>Irreversible and destructive actions</CardDescription>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" disabled={dataLoading}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Account
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Account</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete your account and all associated data. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={deleteAccount}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete Account
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {activeTab !== "account" && (
          <>
            <Separator />

            <div className="flex justify-end pt-6">
              {profileError && !profileLoading && (
                <p className="text-sm text-destructive mr-4 self-center">
                  Could not load your profile. Refresh the page to retry.
                </p>
              )}
              {/* Not gated on `profile`: a null profile used to disable this
                  button with no explanation, which read as a dead button. Let
                  the click through and report whatever the write returns. */}
              <Button onClick={saveSettings} disabled={profileLoading || isSavingSettings}>
                <Save className="h-4 w-4 mr-2" />
                {profileLoading ? "Loading..." : isSavingSettings ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </>
        )}
    </div>
  );
}